import type { CaptureResult, PostHogInterface } from "posthog-js";
import {
    redactContactTokenPaths,
    shortLinkDestinationAnalyticsContext,
    SHORT_LINK_ARRIVAL_TTL_MS,
} from "./short-link-attribution";

const MARKER_PREFIX = "#wags-short-link:v1:";
const SAFE_ALIAS_SLUG = /^[^\u0000-\u001f\u007f]{1,120}$/u;
const SAFE_NONCE = /^([0-9a-z]+)\.([a-z0-9-]{16,64})$/iu;
const ARRIVAL_EXPORT_PROPERTIES = new Set([
    "redirect_request_id",
    "short_link_slug",
    "destination_host",
    "destination_path",
    "browser_session_id",
    "attribution_version",
    "$current_url",
    "$host",
    "$pathname",
    "$referrer",
    "$initial_referrer",
    "$referring_domain",
    "$initial_referring_domain",
    "token",
    "distinct_id",
    "$session_id",
    "$window_id",
    "$process_person_profile",
    "$is_identified",
    "$cookieless_mode",
    "$device_id",
    "$user_id",
    "$lib",
    "$lib_version",
    "environment",
]);

export function scrubShortLinkArrivalCapture(
    capture: CaptureResult | null,
): CaptureResult | null {
    if (!capture || capture.event !== "short_link.destination_arrived") {
        return capture;
    }

    return {
        uuid: capture.uuid,
        event: capture.event,
        ...(capture.timestamp ? { timestamp: capture.timestamp } : {}),
        properties: Object.fromEntries(
            Object.entries(capture.properties).filter(([key]) =>
                ARRIVAL_EXPORT_PROPERTIES.has(key),
            ),
        ),
    };
}

export type ShortLinkArrivalAnalytics = Pick<
    PostHogInterface,
    | "capture"
    | "get_session_id"
    | "get_explicit_consent_status"
    | "has_opted_out_capturing"
> & { is_capturing?: () => boolean };

export type ShortLinkArrivalOutcome =
    | { kind: "none" }
    | {
          kind: "unattributed";
          reason:
              | "invalid_marker"
              | "expired_marker"
              | "analytics_disabled"
              | "consent_declined"
              | "session_unavailable"
              | "capture_blocked"
              | "capture_failed";
      }
    | {
          kind: "prepared";
          redirectRequestId: string;
          slug: string;
          destinationUrl: string;
          destinationHost: string;
          destinationPath: string;
      }
    | { kind: "captured"; redirectRequestId: string };

type PreparedArrival = Extract<ShortLinkArrivalOutcome, { kind: "prepared" }>;

export type ShortLinkArrivalTracker = {
    prepare: (input: {
        href: string;
        replaceState: (canonicalHref: string) => void;
        now?: number;
    }) => ShortLinkArrivalOutcome;
    capture: (input: {
        analyticsEnabled: boolean;
        analytics: ShortLinkArrivalAnalytics;
    }) => ShortLinkArrivalOutcome;
};

function parseMarker(marker: string) {
    if (!marker.startsWith(MARKER_PREFIX)) return null;
    const content = marker.slice(MARKER_PREFIX.length);
    const splitAt = content.lastIndexOf(":");
    if (splitAt <= 0) return null;

    let slug: string;
    try {
        slug = decodeURIComponent(content.slice(0, splitAt));
    } catch {
        return null;
    }
    const redirectRequestId = content.slice(splitAt + 1);
    const nonceMatch = SAFE_NONCE.exec(redirectRequestId);
    if (!SAFE_ALIAS_SLUG.test(slug) || !nonceMatch) return null;

    const createdAt = Number.parseInt(nonceMatch[1], 36);
    if (!Number.isSafeInteger(createdAt)) return null;
    return { slug, redirectRequestId, createdAt };
}

export function createShortLinkArrivalTracker(): ShortLinkArrivalTracker {
    let prepareCalled = false;
    let preparedArrival: PreparedArrival | null = null;
    let terminalOutcome: ShortLinkArrivalOutcome | null = null;

    return {
        prepare({ href, replaceState, now = Date.now() }) {
            if (prepareCalled)
                return terminalOutcome ?? preparedArrival ?? { kind: "none" };
            prepareCalled = true;

            const markerIndex = href.indexOf("#");
            if (markerIndex < 0) {
                terminalOutcome = { kind: "none" };
                return terminalOutcome;
            }

            const marker = href.slice(markerIndex);
            if (!marker.startsWith(MARKER_PREFIX)) {
                terminalOutcome = { kind: "none" };
                return terminalOutcome;
            }

            const canonicalHref = href.slice(0, markerIndex);
            replaceState(canonicalHref);

            let destination: URL;
            try {
                destination = new URL(canonicalHref);
            } catch {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "invalid_marker",
                };
                return terminalOutcome;
            }

            const parsedMarker = parseMarker(marker);
            if (!parsedMarker) {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "invalid_marker",
                };
                return terminalOutcome;
            }

            if (
                !Number.isSafeInteger(now) ||
                parsedMarker.createdAt > now + 5_000 ||
                now - parsedMarker.createdAt > SHORT_LINK_ARRIVAL_TTL_MS
            ) {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "expired_marker",
                };
                return terminalOutcome;
            }

            const destinationContext =
                shortLinkDestinationAnalyticsContext(canonicalHref);
            preparedArrival = {
                kind: "prepared",
                redirectRequestId: parsedMarker.redirectRequestId,
                slug: parsedMarker.slug,
                destinationUrl: destinationContext.destinationUrl,
                destinationHost: destinationContext.destinationHost,
                destinationPath: redactContactTokenPaths(destination.pathname),
            };
            return preparedArrival;
        },
        capture({ analyticsEnabled, analytics }) {
            if (terminalOutcome) return terminalOutcome;
            if (!preparedArrival) return { kind: "none" };
            if (!analyticsEnabled) {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "analytics_disabled",
                };
                return terminalOutcome;
            }
            let isCapturing: boolean;
            try {
                if (analytics.get_explicit_consent_status() === "denied") {
                    terminalOutcome = {
                        kind: "unattributed",
                        reason: "consent_declined",
                    };
                    return terminalOutcome;
                }
                isCapturing =
                    !analytics.has_opted_out_capturing() &&
                    Boolean(analytics.is_capturing?.());
            } catch {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "analytics_disabled",
                };
                return terminalOutcome;
            }
            if (!isCapturing) {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "analytics_disabled",
                };
                return terminalOutcome;
            }

            let browserSessionId: string | null;
            try {
                browserSessionId = analytics.get_session_id();
            } catch {
                browserSessionId = null;
            }
            if (!browserSessionId) {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "session_unavailable",
                };
                return terminalOutcome;
            }

            const eventProperties = {
                redirect_request_id: preparedArrival.redirectRequestId,
                short_link_slug: preparedArrival.slug,
                destination_host: preparedArrival.destinationHost,
                destination_path: preparedArrival.destinationPath,
                browser_session_id: browserSessionId,
                attribution_version: 1,
                $current_url: preparedArrival.destinationUrl,
                $host: preparedArrival.destinationHost,
                $pathname: preparedArrival.destinationPath,
                $referrer: "",
                $initial_referrer: "",
                $referring_domain: "",
                $initial_referring_domain: "",
            };

            try {
                const captureResult = analytics.capture(
                    "short_link.destination_arrived",
                    eventProperties,
                );
                if (!captureResult) {
                    terminalOutcome = {
                        kind: "unattributed",
                        reason: "capture_blocked",
                    };
                    return terminalOutcome;
                }
                terminalOutcome = {
                    kind: "captured",
                    redirectRequestId: preparedArrival.redirectRequestId,
                };
            } catch {
                terminalOutcome = {
                    kind: "unattributed",
                    reason: "capture_failed",
                };
            }
            return terminalOutcome;
        },
    };
}

const shortLinkArrivalTracker = createShortLinkArrivalTracker();

export function prepareShortLinkArrival(input: {
    href: string;
    replaceState: (canonicalHref: string) => void;
    now?: number;
}) {
    return shortLinkArrivalTracker.prepare(input);
}

export function capturePreparedShortLinkArrival(input: {
    analyticsEnabled: boolean;
    analytics: ShortLinkArrivalAnalytics;
}) {
    return shortLinkArrivalTracker.capture(input);
}
